import {
  CARD_FIELD_OPTIONS,
  Container,
  Layouts,
  Layout,
  Integrators,
  CardColor,
  WorkingDays,
  BuilderTrigger,
  BuilderAction,
  Option,
  HexColor,
  WorkingHours,
  ManualCardContents,
  ManualCardFormat,
  EmployeeJobTitle,
  InboxPanelConfig,
  CardContents,
  TaskType,
  Icons,
} from "../types/v2";
import {
  DEFAULT_SCHEDULE_TRIGGER_VALUE,
  encodeScheduleTriggerValue,
} from "./automationSchedule";
import { deepFreeze } from "./deepFreeze";

export const SHOW_ALL_SUGGESTED_TAGS = true;

export const DEFAULT_COMPLETED_ON_TIME_BUFFER_MIN = 10;
export const DEFAULT_CUSTOMER_UPDATE_SNOOZE_MIN = 10;

export const DEFAULT_INBOX_PANEL_CONFIG: InboxPanelConfig = {
  showActivityFeed: true,
  showCustomerUpdates: true,
  showGoList: true,
  showMessages: true,
  showDispatchView: false,
};

// Shop-creation-time defaults only (WEBV3-286) — distinct from
// DEFAULT_INBOX_PANEL_CONFIG above, which remains the fallback read for any
// existing shop with no shopConfig.inboxPanel set. Changing
// DEFAULT_INBOX_PANEL_CONFIG instead would retroactively flip Inbox Panel
// tab visibility for shops created before this shipped.
export const NEW_SHOP_INBOX_PANEL_CONFIG: InboxPanelConfig = {
  showActivityFeed: true,
  showCustomerUpdates: false,
  showGoList: false,
  showMessages: true,
  showDispatchView: true,
};

export const DEFAULT_CARD_PRIMARY_FIELD = CARD_FIELD_OPTIONS[0];
export const DEFAULT_CARD_SECONDARY_FIELD = CARD_FIELD_OPTIONS[2];
export const DEFAULT_CARD_TERTIARY_FIELD = CARD_FIELD_OPTIONS[5];

export const APP_ANALYTICS_DELAY_MINS = 60;
export const PERFORMANCE_MEASUREMENT_DURATION_MS = 2000;
export const PERFORMANCE_INTERVAL_MS = 300000; // 5 minutes
export const APP_ANALYTICS_CHECK_INTERVAL_MS = 300000;
export const SPAN_CAPTURE_RATE = 0.01;

export const ICON_ONLY_HEADER_LINKS = true;

export const TICKER_INTERVAL_MS = 1000; // 1 second

export const SHOW_EMPTY_CONTAINER_PLACEHOLDER = false;

// The maximum zoom level (1.5) is load-bearing: SortableContainer.tsx sizes its
// scroll-region padding to the selection ring in Card.tsx (5px) × this maximum zoom,
// so raising this value requires raising that padding.
export const CARD_ZOOM_LEVELS = [
  0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5,
] as const;

export const TEKMETRIC_STATUSES = [
  { value: 1, label: "Estimate" },
  { value: 2, label: "Work-In-Progress" },
  { value: 3, label: "Complete" },
  { value: 4, label: "Saved for later" },
  { value: 6, label: "Accounts Receiveable" },
];

const UNSELECTABLE_TEKMETRIC_STATUSES = [{ value: 5, label: "Posted" }];

export const ALL_TEKMETRIC_STATUSES = [
  ...TEKMETRIC_STATUSES,
  ...UNSELECTABLE_TEKMETRIC_STATUSES,
];

export const CLOSED_TEKMETRIC_STATUS_VALUES = [3, 6, 5];

export const CLOSED_TEKMETRIC_STATUSES = TEKMETRIC_STATUSES.filter((s) =>
  CLOSED_TEKMETRIC_STATUS_VALUES.includes(s.value)
);

const SHOPWARE_STATUSES = [
  { value: "estimate", label: "Estimate" },
  { value: "in_progress", label: "In Progress" },
];

const UNSELECTABLE_SHOPWARE_STATUSES = [{ value: "invoice", label: "Invoice" }];

export const ALL_SHOPWARE_STATUSES = [
  ...SHOPWARE_STATUSES,
  ...UNSELECTABLE_SHOPWARE_STATUSES,
];

export const CLOSED_SHOPWARE_STATUS_VALUES = ["invoice"];

export const CLOSED_SHOPWARE_STATUSES = SHOPWARE_STATUSES.filter((s) =>
  CLOSED_SHOPWARE_STATUS_VALUES.includes(s.value)
);

const MANUAL_CARD_STATUSES = [
  { value: "complete", label: "Complete" },
  { value: "manuallyClosed", label: "Manually Closed" },
];

export const CLOSED_MANUAL_CARD_STATUS_VALUES = ["complete", "manuallyClosed"];

export const CLOSED_MANUAL_CARD_STATUSES = MANUAL_CARD_STATUSES.filter((s) =>
  CLOSED_MANUAL_CARD_STATUS_VALUES.includes(s.value)
);

export const CLOSED_STATUS_VALUES = [
  ...CLOSED_TEKMETRIC_STATUS_VALUES,
  ...CLOSED_SHOPWARE_STATUS_VALUES,
  ...CLOSED_MANUAL_CARD_STATUS_VALUES,
];

export const CLOSED_STATUSES = [
  ...CLOSED_TEKMETRIC_STATUSES,
  ...CLOSED_SHOPWARE_STATUSES,
  ...CLOSED_MANUAL_CARD_STATUSES,
];

export const STATUS_OPTIONS = {
  [Integrators.tekmetric]: TEKMETRIC_STATUSES,
  [Integrators.shopware]: SHOPWARE_STATUSES,
};

export const JOB_TITLES = {
  [EmployeeJobTitle.serviceAdvisor]: "Service Advisor",
  [EmployeeJobTitle.technician]: "Technician",
  [EmployeeJobTitle.other]: "Other",
};

export const DEFAULT_WORKING_HOURS_START = "08:00";
export const DEFAULT_WORKING_HOURS_END = "18:00";
export const DEFAULT_CUSTOMER_UPDATE_TIME = "10:00";

export const DEFAULT_USE_SHOPHERO_EMPLOYEE_JOB_TITLE = false;
export const DEFAULT_DASHBOARD_SCALING = "noScaling";
export const DEFAULT_SHOW_CONTAINER_LINK_ICONS = false;
export const DEFAULT_SHOW_LABELS_AS_TAGS = false;
export const DEFAULT_SHOW_CARD_TECHNICIAN = true;
export const DEFAULT_SHOW_ADDITIONAL_CARD_TECHNICIANS = true;
export const DEFAULT_TECHNICIAN_WRITEBACK_ENABLED = false;
export const DEFAULT_WORKING_HOURS: WorkingHours = {
  [WorkingDays.Monday]: {
    startTime: DEFAULT_WORKING_HOURS_START,
    endTime: DEFAULT_WORKING_HOURS_END,
    isClosed: false,
  },
  [WorkingDays.Tuesday]: {
    startTime: DEFAULT_WORKING_HOURS_START,
    endTime: DEFAULT_WORKING_HOURS_END,
    isClosed: false,
  },
  [WorkingDays.Wednesday]: {
    startTime: DEFAULT_WORKING_HOURS_START,
    endTime: DEFAULT_WORKING_HOURS_END,
    isClosed: false,
  },
  [WorkingDays.Thursday]: {
    startTime: DEFAULT_WORKING_HOURS_START,
    endTime: DEFAULT_WORKING_HOURS_END,
    isClosed: false,
  },
  [WorkingDays.Friday]: {
    startTime: DEFAULT_WORKING_HOURS_START,
    endTime: DEFAULT_WORKING_HOURS_END,
    isClosed: false,
  },
  [WorkingDays.Saturday]: {
    startTime: DEFAULT_WORKING_HOURS_START,
    endTime: DEFAULT_WORKING_HOURS_END,
    isClosed: true,
  },
  [WorkingDays.Sunday]: {
    startTime: DEFAULT_WORKING_HOURS_START,
    endTime: DEFAULT_WORKING_HOURS_END,
    isClosed: true,
  },
};

export const DEFAULT_TIMER_VALUE = "15";
export const DEFAULT_REFRESH_TIME_IN_SECONDS = 30;

export const DEFAULT_CONTAINER_NAME = "dispatch";
export const DEFAULT_CONTAINERS: Container[] = [
  { id: DEFAULT_CONTAINER_NAME, tags: [] },
];
const DEFAULT_LAYOUT: Layout = {
  h: 2,
  i: DEFAULT_CONTAINER_NAME,
  moved: false,
  static: false,
  w: 96,
  x: 0,
  y: 0,
};

export const DEFAULT_LAYOUTS: Layouts = [{ "Main Layout": [DEFAULT_LAYOUT] }];

// Seeded into shops/{shopId}/misc/tags at registration (WEBV3-108).
// List per WEBV3-287; colors are unaffected (WEBV3-191 seeds all default tags white).
export const DEFAULT_TAGS: string[] = [
  "Waiter",
  "Tow-in",
  "Comeback",
  "Due Today",
  "Car Not Here",
  "Shuttle",
  "A/C",
  "Brakes",
  "CEL",
  "Diag",
  "LOF",
  "Electrical",
  "Engine",
  "Testing",
];

export const DEFAULT_STATUSES = {
  // WEBV3-286: Work-In-Progress + Complete on by default, Estimate off.
  [Integrators.tekmetric]: [
    STATUS_OPTIONS[Integrators.tekmetric][1].value, // Work-In-Progress
    STATUS_OPTIONS[Integrators.tekmetric][2].value, // Complete
  ].map((s) => `${s}`),
  // Shopware has no selectable "Complete"-equivalent status (see
  // SHOPWARE_STATUSES above) — left as-is.
  [Integrators.shopware]: [STATUS_OPTIONS[Integrators.shopware][1].value],
};

export const DEFAULT_CARD_LABEL_WRAP = "no";
export const DEFAULT_CARD_TIMER_UNITS = "minutes";

export const DEFAULT_CARD_CONTENTS: CardContents = {
  primaryField: DEFAULT_CARD_PRIMARY_FIELD,
  secondaryField: DEFAULT_CARD_SECONDARY_FIELD,
  tertiaryField: DEFAULT_CARD_TERTIARY_FIELD,
  promiseTimeEnabled: false,
  serviceAdvisorEnabled: false,
  technicianEnabled: false,
  jobHoursEnabled: false,
  manualCardFormat: "basic",
  waiterIconEnabled: false,
  manualServiceAdvisorEnabled: false,
  manualTechnicianEnabled: false,
  manualJobHoursEnabled: false,
  manualDueInTimeEnabled: false,
  manualDueOutTimeEnabled: false,
  manualTagsEnabled: false,
  manualWaiterEnabled: false,
};

// Shop-creation-time defaults only (WEBV3-190) — distinct from DEFAULT_CARD_CONTENTS above,
// which remains the all-off fallback read for every existing shop that hasn't set these
// fields. Changing DEFAULT_CARD_CONTENTS instead would retroactively flip card-display
// behavior for shops created before this shipped.
export const NEW_SHOP_CARD_CONTENTS: CardContents = {
  primaryField: DEFAULT_CARD_PRIMARY_FIELD,
  secondaryField: DEFAULT_CARD_SECONDARY_FIELD,
  // Deliberately NOT DEFAULT_CARD_TERTIARY_FIELD (CARD_FIELD_OPTIONS[5], "YY
  // Model") — that constant also feeds DEFAULT_CARD_CONTENTS, the fallback
  // for every pre-existing shop with an unset tertiaryField, and must not
  // change. New shops (WEBV3-286) get CARD_FIELD_OPTIONS[4] directly.
  tertiaryField: CARD_FIELD_OPTIONS[4],
  serviceAdvisorEnabled: true,
  technicianEnabled: true,
  jobHoursEnabled: true,
  waiterIconEnabled: true,
  promiseTimeEnabled: false,
  manualServiceAdvisorEnabled: true,
  manualTechnicianEnabled: true,
  manualJobHoursEnabled: true,
  manualTagsEnabled: true,
  manualCustomerInfoEnabled: true,
  manualVehicleInfoEnabled: true,
  // Explicit empty array, not omitted: an unset requiredVehicleFields falls back to
  // ALL vehicle fields in CardForm.tsx, which combined with manualVehicleInfoEnabled: true
  // would make year/make/model mandatory on every manual card save. [] keeps vehicle info
  // display-only, matching what WEBV3-190 actually asked for.
  requiredVehicleFields: [],
  manualFinancialFieldsEnabled: true,
  manualMarkCompleteEnabled: true,
  manualWaiterEnabled: true,
  manualDueInTimeEnabled: false,
  manualDueOutTimeEnabled: true,
};

export const COLOR_PICKER_COLORS: {
  hex: HexColor;
  label: string;
  name: string;
}[] = [
  {
    hex: "#FF7B7B",
    label: "cpRed",
    name: "Red",
  },
  {
    hex: "#FFD18D",
    label: "cpLightOrange",
    name: "Light Orange",
  },
  {
    hex: "#CCFF1D",
    label: "cpYellow",
    name: "Yellow",
  },
  {
    hex: "#65FF60",
    label: "cpGreen",
    name: "Green",
  },
  {
    hex: "#A8B4FF",
    label: "cpPurple",
    name: "Purple",
  },
  {
    hex: "#C880FF",
    label: "cpVibrantPurple",
    name: "Vibrant Purple",
  },
  {
    hex: "#47FFFF",
    label: "cpCyan",
    name: "Cyan",
  },
  {
    hex: "#FF85D0",
    label: "cpPink",
    name: "Pink",
  },
  {
    hex: "#FFD700",
    label: "cpGold",
    name: "Gold",
  },
  {
    hex: "#FFA500",
    label: "cpOrange",
    name: "Orange",
  },
  {
    hex: "#A9A9A9",
    label: "cpDarkGrey",
    name: "Dark Grey",
  },
  {
    hex: "#E2E2E2",
    label: "cpLightGrey",
    name: "Light Grey",
  },
];

export const DEFAULT_COLORS: CardColor[] = COLOR_PICKER_COLORS.map(
  (color, index) => ({
    id: `defaultColor-${index}`,
    color: color.hex,
    title: color.name,
    description: "",
  })
);

// Shop-creation-time card color legend (WEBV3-286) — same 12 colors/ids/hex
// as DEFAULT_COLORS (the fallback for shops with no cardConfig.cardColors
// set), with 5 of them relabeled to the meaning they carry on a new shop's
// board. Title strings here are referenced by exact match from WEBV3-288
// (default automations, e.g. "Change card color to Waiter") — keep them in
// sync if either changes.
const NEW_SHOP_CARD_COLOR_LEGEND: Record<string, string> = {
  Red: "Overdue",
  Green: "Due Today",
  Gold: "Needs Attention",
  "Light Grey": "Drop-off (default)",
  Cyan: "Waiter",
};

export const NEW_SHOP_CARD_COLORS: CardColor[] = DEFAULT_COLORS.map(
  (color) => ({
    ...color,
    title: NEW_SHOP_CARD_COLOR_LEGEND[color.title] ?? color.title,
  })
);

export const MANUAL_CARD_CONFIG_OPTIONS: {
  [k in ManualCardContents]: {
    label: string;
    description: string;
    type: ManualCardFormat;
  };
} = {
  [ManualCardContents.manualJobHoursEnabled]: {
    label: "Job Hours",
    type: "advanced",
    description: "Allow entering job hours on manual cards.",
  },
  [ManualCardContents.manualTechnicianEnabled]: {
    label: "Technician",
    type: "basic",
    description: "Allow selecting from a list of technicians on manual cards.",
  },
  [ManualCardContents.manualServiceAdvisorEnabled]: {
    label: "Service Advisor",
    type: "basic",
    description:
      "Allow selecting from a list of service advisors on manual cards.",
  },
  [ManualCardContents.manualDueInTimeEnabled]: {
    label: "Due In Time",
    type: "advanced",
    description: "Allow entering the due in, or drop off time on manual cards.",
  },
  [ManualCardContents.manualDueOutTimeEnabled]: {
    label: "Due Out Time",
    type: "advanced",
    description: "Allow entering the due out, or promise time on manual cards.",
  },
  [ManualCardContents.manualAppointmentEnabled]: {
    label: "Appointment",
    type: "advanced",
    description:
      "Allow entering appointment start and end times on manual cards.",
  },
  [ManualCardContents.manualTagsEnabled]: {
    label: "Tags",
    type: "advanced",
    description: "Allow entering tags on manual cards.",
  },
  [ManualCardContents.manualWaiterEnabled]: {
    label: "Waiter",
    type: "advanced",
    description: "Allow flagging cards as being waiters.",
  },
  [ManualCardContents.manualCustomerInfoEnabled]: {
    label: "Customer Information",
    type: "basic",
    description:
      "Allow entering detailed customer information on manual cards.",
  },
  [ManualCardContents.manualVehicleInfoEnabled]: {
    label: "Vehicle Information",
    type: "basic",
    description: "Allow entering detailed vehicle information on manual cards.",
  },
  [ManualCardContents.manualFinancialFieldsEnabled]: {
    label: "Financial Information",
    type: "advanced",
    description:
      "Allow entering labor sales, parts sales, and completion date on manual cards.",
  },
  [ManualCardContents.autoIncrementCardNumberEnabled]: {
    label: "Auto-increment Repair Order Numbers",
    type: "basic",
    description:
      "Automatically suggest the next sequential number when creating new repair orders.",
  },
  [ManualCardContents.manualMarkCompleteEnabled]: {
    label: "Mark Complete",
    type: "basic",
    description:
      "Allow marking cards as complete when creating or editing manual cards.",
  },
};

// Flat default for every newly created employee (WEBV3-286) — replaces the
// old hash-selected color from EMPLOYEE_DEFAULT_COLORS below for NEW
// employees only. EMPLOYEE_DEFAULT_COLORS/getEmployeeColor stay as-is for
// the v1→v2 migration path (employee.transformV1ToV2), which is unrelated.
export const DEFAULT_EMPLOYEE_COLOR = "#CCCCCC";

export const EMPLOYEE_DEFAULT_COLORS = [
  "#001f3f", // Dark Blue
  "#006400", // Dark Green
  "#FF8C00", // Dark Orange
  "#4B0082", // Dark Purple
  "#800000", // Dark Maroon
  "#8B0000", // Dark Red
  "#2F4F4F", // Dark Gray
  "#D87093", // Dark Pink
  "#8B4513", // Dark Brown
  "#0C0C0C", // Almost Black
  "#661616",
  "#663716",
  "#665716",
  "#667716",
  "#4c6616",
  "#3c6616",
  "#2c6616",
  "#165221",
  "#165241",
  "#165261",
  "#165282",
];

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  [TaskType.GENERAL]: "General",
  [TaskType.UPDATE]: "Update",
  [TaskType.PARTS]: "Parts",
  [TaskType.OTHER]: "Other",
};

export const TASK_TYPE_COLORS: Record<TaskType, string> = {
  [TaskType.GENERAL]: "#6a7282", // gray-500
  [TaskType.UPDATE]: "#f0b100", // yellow-500
  [TaskType.PARTS]: "#ff6900", // orange-500
  [TaskType.OTHER]: "#2b7fff", // blue-500
};

export const TYPE_BADGE_CLASSES: Record<TaskType, string> = {
  [TaskType.GENERAL]: "bg-gray-100 text-gray-800",
  [TaskType.UPDATE]: "bg-yellow-100 text-yellow-800",
  [TaskType.PARTS]: "bg-orange-100 text-orange-800",
  [TaskType.OTHER]: "bg-blue-100 text-blue-800",
};

export const TAG_COLORS = [
  // Pinks
  "#D53F8C", // Hot Pink (darkest)
  "#F687B3", // Pink
  "#FBB6CE", // Light Pink
  "#FEEBC8", // Lightest Pink
  // Reds/Pinks
  "#E53E3E", // Red (darker)
  "#F56565", // Red
  "#FC8181", // Light Red
  "#FEB2B2", // Pink
  // Browns
  "#744210", // Dark Brown
  "#975A16", // Medium Brown
  "#B7791F", // Light Brown
  "#D69E2E", // Golden Brown
  // Yellows/Oranges
  "#DD6B20", // Dark Orange
  "#ED8936", // Orange
  "#F6AD55", // Light Orange
  "#ECC94B", // Yellow
  // Greens
  "#38A169", // Green (darker)
  "#48BB78", // Green
  "#68D391", // Light Green
  "#9AE6B4", // Mint
  // Teals/Cyans
  "#38B2AC", // Teal (darkest)
  "#4FD1C5", // Teal (lighter)
  "#81E6D9", // Cyan
  "#B2F5EA", // Light Cyan
  // Blues
  "#2B6CB0", // Royal Blue (darkest)
  "#3182CE", // Blue (darker)
  "#4299E1", // Blue
  "#90CDF4", // Light Blue
  // Purples
  "#805AD5", // Purple (darker)
  "#9F7AEA", // Purple
  "#B794F4", // Light Purple
  "#D6BCFA", // Lavender
  // Grays
  "#4A5568", // Gray (darker)
  "#718096", // Gray
  "#A0AEC0", // Light Gray
  "#CBD5E0", // Extra Light Gray
  // Neutral
  "#FFFFFF", // White
  "#030712", // Black
];

export const DEFAULT_TIMER_RUNNING_UNDER = "bg-warning";
// red-600 hex — the Tailwind config only defines a flat `red` (#FF0000), not a
// numbered scale, so `bg-red-600` would generate no style. Use the arbitrary value.
export const DEFAULT_TIMER_RUNNING_OVER = "bg-[#DC2626]";

export const DEFAULT_CARD_COLOR = "#E2E2E2";
export const DEFAULT_TAG_COLOR = "#4FD1C5";
export const PROMISE_TIME_PAST_COLOR = "#FF7B7B";
export const DEFAULT_CONTAINER_ID = "dispatch";

export const DEFAULT_PAGINATION_SIZE = 25;

export const AUTOMATION_TRIGGER_OPTIONS: BuilderTrigger[] = deepFreeze([
  {
    // When a card is created
    id: "trigger-001",
    triggerObject: "card",
    triggerEventType: "onCreate",
    triggerField: null,
    triggerFieldChange: null,
    triggerCondition: null,
    triggerValueEditable: false,
    triggerValue: null,
    buttonOptions: {
      text: "When a card is created",
      icon: Icons.cardCreatedTrigger,
    },
    conditionalTriggers: [
      {
        id: "trigger-001-1",
        triggerObject: "card",
        triggerEventType: "onCreate",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: true,
        buttonOptions: {
          text: "And is a waiter",
          icon: Icons.isWaiter,
        },
      },
      {
        id: "trigger-001-9",
        triggerObject: "card",
        triggerEventType: "onCreate",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: false,
        buttonOptions: {
          text: "And is a drop-off",
          icon: Icons.isDropOff,
        },
      },
      {
        id: "trigger-001-2",
        triggerObject: "card",
        triggerEventType: "onCreate",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-001-4",
        triggerObject: "card",
        triggerEventType: "onCreate",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-001-7",
        triggerObject: "card",
        triggerEventType: "onCreate",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-001-5",
        triggerObject: "card",
        triggerEventType: "onCreate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-001-6",
        triggerObject: "card",
        triggerEventType: "onCreate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-001-8",
        triggerObject: "card",
        triggerEventType: "onCreate",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    // When card is moved into a container
    id: "trigger-002",
    triggerObject: "card",
    triggerEventType: "onUpdate",
    triggerField: "currentContainer",
    triggerFieldChange: "changedTo",
    triggerCondition: null,
    triggerValueEditable: false,
    triggerValue: null,
    buttonOptions: {
      text: "When a card is moved into a container",
      icon: Icons.cardMovedIntoContainer,
    },
    conditionalTriggers: [
      {
        id: "trigger-002-1",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-002-2",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "color",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "colors",
        triggerValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        triggerPlaceholder: "Blue",
        buttonOptions: {
          text: "And Card Color is set to",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "trigger-002-3",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-002-7",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-002-4",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: true,
        triggerPlaceholder: "is",
        buttonOptions: {
          text: "And is a waiter",
          icon: Icons.isWaiter,
        },
      },
      {
        id: "trigger-002-9",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: false,
        triggerPlaceholder: "is",
        buttonOptions: {
          text: "And is a drop-off",
          icon: Icons.isDropOff,
        },
      },
      {
        id: "trigger-002-5",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-002-6",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-002-8",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    // When card is moved into a container
    id: "trigger-003",
    triggerObject: "card",
    triggerEventType: "onUpdate",
    triggerField: "currentContainer",
    triggerFieldChange: "changedFrom",
    triggerCondition: null,
    triggerValueEditable: false,
    triggerValue: null,
    buttonOptions: {
      text: "When a card is moved out of a container",
      icon: Icons.cardMovedOutOfContainer,
    },
    conditionalTriggers: [
      {
        id: "trigger-003-1",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "currentContainer",
        triggerFieldChange: "changedFrom",
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name was",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-003-2",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "color",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "colors",
        triggerValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        triggerPlaceholder: "Blue",
        buttonOptions: {
          text: "And Card Color is set to",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "trigger-003-3",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-003-7",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-003-4",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: true,
        triggerPlaceholder: "is",
        buttonOptions: {
          text: "And is a waiter",
          icon: Icons.isWaiter,
        },
      },
      {
        id: "trigger-003-9",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: false,
        triggerPlaceholder: "is",
        buttonOptions: {
          text: "And is a drop-off",
          icon: Icons.isDropOff,
        },
      },
      {
        id: "trigger-003-5",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-003-6",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-003-8",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    id: "trigger-004",
    triggerObject: "card",
    triggerEventType: "onUpdate",
    triggerField: "isWaiter",
    triggerFieldChange: "changedTo",
    triggerCondition: "is",
    triggerValueEditable: false,
    triggerValue: true,
    triggerPlaceholder: "is",
    buttonOptions: {
      text: "When a card changes to a waiter",
      icon: Icons.cardChangesToWaiter,
    },
    conditionalTriggers: [
      {
        id: "trigger-004-2",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-004-4",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-004-7",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-004-5",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-004-6",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-004-8",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    // The inverse of trigger-004: fires when isWaiter changes to false, i.e.
    // the customer is dropping the vehicle off rather than waiting.
    id: "trigger-005",
    triggerObject: "card",
    triggerEventType: "onUpdate",
    triggerField: "isWaiter",
    triggerFieldChange: "changedTo",
    triggerCondition: "is",
    triggerValueEditable: false,
    triggerValue: false,
    triggerPlaceholder: "is",
    buttonOptions: {
      text: "When a card changes to a drop off",
      icon: Icons.cardChangesToDropOff,
    },
    conditionalTriggers: [
      {
        id: "trigger-005-2",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-005-4",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-005-7",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-005-5",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-005-6",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-005-8",
        triggerObject: "card",
        triggerEventType: "onUpdate",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    // Time-based: fires a configurable number of minutes before the card's
    // promised time. Evaluated by the scheduled promise-time scanner
    // (functions-v2/src/functions/promiseTime), not the reactive onCardWrite
    // path. triggerValue is the lead time in minutes.
    id: "trigger-006",
    triggerObject: "card",
    triggerEventType: "onPromiseTime",
    triggerField: "promiseTimeApproaching",
    triggerFieldChange: null,
    triggerCondition: null,
    triggerValueEditable: true,
    triggerValue: 30,
    triggerPlaceholder: "30",
    buttonOptions: {
      text: "When promise time is approaching (minutes before)",
      icon: Icons.whenPromiseTimeDue,
    },
    conditionalTriggers: [
      {
        id: "trigger-006-1",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: true,
        buttonOptions: {
          text: "And is a waiter",
          icon: Icons.isWaiter,
        },
      },
      {
        id: "trigger-006-9",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: false,
        buttonOptions: {
          text: "And is a drop-off",
          icon: Icons.isDropOff,
        },
      },
      {
        id: "trigger-006-2",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-006-3",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "color",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "colors",
        triggerValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        triggerPlaceholder: "Blue",
        buttonOptions: {
          text: "And Card Color is set to",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "trigger-006-4",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-006-5",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-006-6",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-006-7",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-006-8",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    // Time-based: fires at (or triggerValue minutes after) the promised time.
    id: "trigger-007",
    triggerObject: "card",
    triggerEventType: "onPromiseTime",
    triggerField: "promiseTimeOverdue",
    triggerFieldChange: null,
    triggerCondition: null,
    triggerValueEditable: false,
    triggerValue: 0,
    buttonOptions: {
      text: "When promise time is overdue",
      icon: Icons.whenPromiseTimeDue,
    },
    conditionalTriggers: [
      {
        id: "trigger-007-1",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: true,
        buttonOptions: {
          text: "And is a waiter",
          icon: Icons.isWaiter,
        },
      },
      {
        id: "trigger-007-9",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: false,
        buttonOptions: {
          text: "And is a drop-off",
          icon: Icons.isDropOff,
        },
      },
      {
        id: "trigger-007-2",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-007-3",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "color",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "colors",
        triggerValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        triggerPlaceholder: "Blue",
        buttonOptions: {
          text: "And Card Color is set to",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "trigger-007-4",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-007-5",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-007-6",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-007-7",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-007-8",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    // Time-based: fires once while the card's promised time falls on the
    // current shop-local calendar day. Evaluated by the scheduled
    // promise-time scanner (functions-v2/src/functions/promiseTime), which
    // resolves the shop's timezone only when a shop configures this trigger
    // — see resolveShopTimeZoneForScan in queuePromiseTimeAutomations.ts.
    // Re-arms (can fire again) if the promised time changes. Carries no
    // configurable value.
    id: "trigger-009",
    triggerObject: "card",
    triggerEventType: "onPromiseTime",
    triggerField: "promiseTimeIsToday",
    triggerFieldChange: null,
    triggerCondition: null,
    triggerValueEditable: false,
    triggerValue: null,
    buttonOptions: {
      text: "When promise time is today",
      icon: Icons.whenPromiseTimeDue,
    },
    conditionalTriggers: [
      {
        id: "trigger-009-1",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: true,
        buttonOptions: {
          text: "And is a waiter",
          icon: Icons.isWaiter,
        },
      },
      {
        id: "trigger-009-9",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: false,
        buttonOptions: {
          text: "And is a drop-off",
          icon: Icons.isDropOff,
        },
      },
      {
        id: "trigger-009-2",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-009-3",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "color",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "colors",
        triggerValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        triggerPlaceholder: "Blue",
        buttonOptions: {
          text: "And Card Color is set to",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "trigger-009-4",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-009-5",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-009-6",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-009-7",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-009-8",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    // Fires once when the card's promised time stops being the shop-local
    // "today" calendar day — either via an edit (evaluated reactively by
    // onCardWrite) or a midnight rollover with no edit (evaluated by the
    // scheduled promise-time scanner, functions-v2/src/functions/promiseTime).
    // Both mechanisms resolve the shop's timezone only when a shop
    // configures this trigger. Does not fire on the promised time-of-day
    // passing while still calendar-today — see "When promise time is
    // overdue" for that. Carries no configurable value.
    id: "trigger-010",
    triggerObject: "card",
    triggerEventType: "onPromiseTime",
    triggerField: "promiseTimeChangedFromToday",
    triggerFieldChange: null,
    triggerCondition: null,
    triggerValueEditable: false,
    triggerValue: null,
    buttonOptions: {
      text: "When promise time changes from today",
      icon: Icons.whenPromiseTimeDue,
    },
    conditionalTriggers: [
      {
        id: "trigger-010-1",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: true,
        buttonOptions: {
          text: "And is a waiter",
          icon: Icons.isWaiter,
        },
      },
      {
        id: "trigger-010-9",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: false,
        buttonOptions: {
          text: "And is a drop-off",
          icon: Icons.isDropOff,
        },
      },
      {
        id: "trigger-010-2",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-010-3",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "color",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "colors",
        triggerValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        triggerPlaceholder: "Blue",
        buttonOptions: {
          text: "And Card Color is set to",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "trigger-010-4",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-010-5",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-010-6",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-010-7",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-010-8",
        triggerObject: "card",
        triggerEventType: "onPromiseTime",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
    ],
  },
  {
    // On a schedule. Fired by the scheduled scanner, not by onCardWrite:
    // at the configured shop-local minute it sweeps the shop's open cards and
    // applies the actions to every card matching the conditionals below.
    id: "trigger-008",
    triggerObject: "card",
    triggerEventType: "onSchedule",
    triggerField: null,
    triggerFieldChange: null,
    triggerCondition: null,
    triggerValueEditable: true,
    triggerValueEditor: "schedule",
    triggerValue: encodeScheduleTriggerValue(DEFAULT_SCHEDULE_TRIGGER_VALUE),
    buttonOptions: {
      text: "On a schedule",
      icon: Icons.whenScheduledTimeOccurs,
    },
    conditionalTriggers: [
      {
        id: "trigger-008-1",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: true,
        buttonOptions: {
          text: "And is a waiter",
          icon: Icons.isWaiter,
        },
      },
      {
        id: "trigger-008-9",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "isWaiter",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: false,
        triggerValue: false,
        buttonOptions: {
          text: "And is a drop-off",
          icon: Icons.isDropOff,
        },
      },
      {
        id: "trigger-008-2",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "currentContainer",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueOptionsDynamic: "containers",
        triggerValueEditable: true,
        triggerValue: null,
        triggerPlaceholder: "Dispatch",
        buttonOptions: {
          text: "And Container Name is",
          icon: Icons.containerNameIs,
        },
      },
      {
        id: "trigger-008-4",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "technicianId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "technicians",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And technician is",
          icon: Icons.technicianNameIs,
        },
      },
      {
        id: "trigger-008-7",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "serviceWriterId",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "serviceAdvisors",
        triggerValueOptions: [],
        triggerPlaceholder: "John Doe",
        buttonOptions: {
          text: "And service advisor is",
          icon: Icons.serviceAdvisorNameIs,
        },
      },
      {
        id: "trigger-008-5",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isGreaterThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is greater than",
          icon: Icons.estimatedLaborGreater,
        },
      },
      {
        id: "trigger-008-6",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "totalLaborHours.estimatedLabor",
        triggerFieldChange: null,
        triggerCondition: "isLessThan",
        triggerValueEditable: true,
        triggerValue: 5,
        triggerPlaceholder: "5",
        buttonOptions: {
          text: "And estimated labor is less than",
          icon: Icons.estimatedLaborLess,
        },
      },
      {
        id: "trigger-008-8",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "vehicle.make",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "vehicleMakes",
        triggerPlaceholder: "Toyota",
        buttonOptions: {
          text: "And vehicle make is",
          icon: Icons.vehicleMakeIs,
        },
      },
      {
        id: "trigger-008-3",
        triggerObject: "card",
        triggerEventType: "onSchedule",
        triggerField: "color",
        triggerFieldChange: null,
        triggerCondition: "is",
        triggerValueEditable: true,
        triggerValue: null,
        triggerValueOptionsDynamic: "colors",
        triggerValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        triggerPlaceholder: "Blue",
        buttonOptions: {
          text: "And Card Color is set to",
          icon: Icons.setCardColor,
        },
      },
    ],
  },
]);

export const DEFAULT_EXPIRATION_TIMES: Option[] = [
  { value: "1", label: "1 minute" },
  { value: "5", label: "5 minutes" },
  { value: "10", label: "10 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "20", label: "20 minutes" },
  { value: "25", label: "25 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "120", label: "2 hours" },
];

export const AUTOMATION_ACTION_OPTIONS: BuilderAction[] = deepFreeze([
  {
    // Action-001 has been removed
    id: "action-002",
    actionObject: "card",
    actionEventType: "update",
    actionField: "automationAction",
    actionValue: "wiggle",
    actionValueEditable: false,
    buttonOptions: {
      tooltip: "Wiggles the card.",
      text: "Wiggle Card",
      icon: Icons.wiggleCard,
    },
    subsequentActions: [
      {
        id: "action-002-01",
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionExpireAt",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the wiggle action for the specified time.",
          text: "For X minutes",
          selectedText: "For %{value} minutes",
          icon: Icons.clock,
        },
      },
      {
        id: "action-002-02",
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionStartAt",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the wiggle action after the specified time.",
          text: "After X minutes",
          selectedText: "After %{value} minutes",
          icon: Icons.clock,
        },
      },
    ],
  },
  {
    id: "action-003",
    actionObject: "card",
    actionEventType: "update",
    actionField: "automationAction",
    actionValue: "color",
    actionValueEditable: false,
    actionPlaceholder: "Color",
    buttonOptions: {
      tooltip:
        /* eslint-disable-next-line */
        'Sets a temporary color on the card. If you want to set the color without the ability to reset, choose the "Change card color" action.',
      text: "Set card color",
      icon: Icons.setCardColor,
    },
    subsequentActions: [
      {
        id: "action-003-01",
        required: true,
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionData",
        actionValue: null,
        actionValueEditable: true,
        actionValueOptionsDynamic: "colors",
        actionValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        actionPlaceholder: "red",
        buttonOptions: {
          text: "To ",
          selectedText: "to ",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "action-003-02",
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionExpireAt",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the color change action for the specified time.",
          text: "For X minutes",
          selectedText: "For %{value} minutes",
          icon: Icons.clock,
        },
      },
      {
        id: "action-003-03",
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionStartAt",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the color change action after the specified time.",
          text: "After X minutes",
          selectedText: "After %{value} minutes",
          icon: Icons.clock,
        },
      },
    ],
  },
  {
    id: "action-004",
    actionObject: "card",
    actionEventType: "update",
    actionField: "automationAction",
    actionValue: "colorAndWiggle",
    actionValueEditable: false,
    actionPlaceholder: "Color",
    buttonOptions: {
      tooltip:
        "Wiggles the card and sets the color. Useful if you want to wiggle a card and set a temporary color at the same time.",
      text: "Wiggle and set card color",
      icon: Icons.wiggleAndSetCardColor,
    },
    subsequentActions: [
      {
        id: "action-004-01",
        required: true,
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionData",
        actionValue: null,
        actionValueEditable: true,
        actionValueOptionsDynamic: "colors",
        actionValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        actionPlaceholder: "red",
        buttonOptions: {
          text: "To ",
          selectedText: "to ",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "action-004-02",
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionExpireAt",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the wiggle and color action for the specified time.",
          text: "For X minutes",
          selectedText: "For %{value} minutes",
          icon: Icons.clock,
        },
      },
      {
        id: "action-004-03",
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionStartAt",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip:
            "Apply the wiggle and color action after the specified time.",
          text: "After X minutes",
          selectedText: "After %{value} minutes",
          icon: Icons.clock,
        },
      },
    ],
  },
  {
    id: "action-005",
    actionObject: "card",
    actionEventType: "reset",
    actionField: null,
    actionValue: null,
    actionValueEditable: false,
    buttonOptions: {
      tooltip:
        "Resets all applied automation actions. Useful if you want to reset wiggle and color changes when moving into a specific container.",
      text: "Reset automated changes",
      icon: Icons.resetAutomated,
    },
  },
  {
    id: "action-006",
    actionObject: "card",
    actionEventType: "update",
    actionField: "automationAction",
    actionValue: "flash",
    actionValueEditable: false,
    actionPlaceholder: "Color",
    buttonOptions: {
      tooltip: "Flashes the card.",
      text: "Flash color",
      icon: Icons.flashColor,
    },
    subsequentActions: [
      {
        id: "action-006-01",
        required: true,
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionData",
        actionValue: null,
        actionValueEditable: true,
        actionValueOptionsDynamic: "colors",
        actionValueOptions: DEFAULT_COLORS.map((c) => ({
          value: c.color,
          label: c.title,
        })),
        actionPlaceholder: "red",
        buttonOptions: {
          text: "To ",
          selectedText: "to ",
          icon: Icons.setCardColor,
        },
      },
      {
        id: "action-006-02",
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionExpireAt",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the flash action for the specified time.",
          text: "For X minutes",
          selectedText: "For %{value} minutes",
          icon: Icons.clock,
        },
      },
      {
        id: "action-006-03",
        actionObject: "card",
        actionEventType: "update",
        actionField: "automationActionStartAt",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the flash action after the specified time.",
          text: "After X minutes",
          selectedText: "After %{value} minutes",
          icon: Icons.clock,
        },
      },
    ],
  },
  {
    id: "action-007",
    actionObject: "card",
    actionEventType: "update",
    actionField: "color",
    actionValue: null,
    actionValueEditable: true,
    actionValueOptionsDynamic: "colors",
    actionValueOptions: DEFAULT_COLORS.map((c) => ({
      value: c.color,
      label: c.title,
    })),
    actionPlaceholder: "red",
    buttonOptions: {
      tooltip:
        "Change the card color without being able to reset the change. Useful if you want to combine timed actions with color.",
      text: "Change card color to",
      icon: Icons.changeCardColor,
    },
  },
  {
    id: "action-008",
    actionObject: "card",
    actionEventType: "create",
    actionField: "createAndScheduleCustomerUpdate",
    actionValue: null,
    actionValueEditable: true,
    actionPlaceholder: "60",
    buttonOptions: {
      tooltip:
        "Creates and schedules a customer update for the card. The update will be scheduled for the specified number of minutes in the future.",
      text: "Create customer update scheduled for X minutes from trigger",
      selectedText:
        "Create customer update and schedule for %{value} minutes from the trigger",
      icon: Icons.createCustomerUpdate,
    },
  },
  {
    id: "action-createTask",
    actionObject: "card",
    actionEventType: "create",
    actionField: "createTask",
    actionValue: null,
    actionValueEditable: true,
    buttonOptions: {
      tooltip:
        "Creates a task on the card when the automation fires. Requires a change-based trigger.",
      text: "Create a task",
      selectedText: "Create a task",
      icon: Icons.createATask,
    },
  },
  {
    id: "action-showToast",
    actionObject: "card",
    actionEventType: "update",
    actionField: "showToast",
    actionValue: null,
    actionValueEditable: true,
    buttonOptions: {
      tooltip:
        "Shows a toast notification on the board to everyone viewing it. Requires a change-based trigger.",
      text: "Show a toast notification",
      selectedText: "Show a toast notification",
      icon: Icons.showAToast,
    },
  },
  {
    id: "action-009",
    actionObject: "card",
    actionEventType: "update",
    actionField: "timer",
    actionValue: null,
    actionValueEditable: true,
    actionPlaceholder: "30",
    buttonOptions: {
      tooltip:
        "Starts a running timer on the card set to the specified number of minutes. Useful when a card is moved into a specific container.",
      text: "Start a timer for X minutes",
      selectedText: "Start a %{value} minute timer",
      icon: Icons.startATimer,
    },
  },
  {
    id: "action-010",
    actionObject: "card",
    actionEventType: "update",
    actionField: "stopTimer",
    actionValue: null,
    actionValueEditable: false,
    buttonOptions: {
      tooltip:
        "Stops and clears any running timer on the card. Useful when a card leaves a container or reaches a completed state.",
      text: "Stop the timer",
      selectedText: "Stop the timer",
      icon: Icons.stopATimer,
    },
  },
  {
    id: "action-011",
    actionObject: "card",
    actionEventType: "update",
    actionField: "addTags",
    actionValue: null,
    actionValueEditable: true,
    actionValueOptionsDynamic: "tags",
    multiSelect: true,
    buttonOptions: {
      tooltip:
        "Adds one or more existing tags to the card. Tags already on the card are left unchanged.",
      text: "Add tags",
      selectedText: "Add the tags %{value}",
      icon: Icons.addTags,
    },
    subsequentActions: [
      {
        id: "action-011-01",
        actionObject: "card",
        actionEventType: "create",
        actionField: "addTagsDelayMinutes",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the tag change after the specified time.",
          text: "After X minutes",
          selectedText: "After %{value} minutes",
          icon: Icons.clock,
        },
      },
    ],
  },
  {
    id: "action-012",
    actionObject: "card",
    actionEventType: "update",
    actionField: "removeTags",
    actionValue: null,
    actionValueEditable: true,
    actionValueOptionsDynamic: "tags",
    multiSelect: true,
    buttonOptions: {
      tooltip:
        "Removes one or more tags from the card. Tags not present on the card are ignored.",
      text: "Remove tags",
      selectedText: "Remove the tags %{value}",
      icon: Icons.removeTags,
    },
    subsequentActions: [
      {
        id: "action-012-01",
        actionObject: "card",
        actionEventType: "create",
        actionField: "removeTagsDelayMinutes",
        actionValue: null,
        actionValueEditable: true,
        actionPlaceholder: "5",
        buttonOptions: {
          tooltip: "Apply the tag change after the specified time.",
          text: "After X minutes",
          selectedText: "After %{value} minutes",
          icon: Icons.clock,
        },
      },
    ],
  },
  {
    id: "action-013",
    actionObject: "card",
    actionEventType: "update",
    actionField: "resetTimer",
    actionValue: null,
    actionValueEditable: false,
    buttonOptions: {
      tooltip:
        "Restarts the card's timer from zero, keeping whatever limit is already set. Useful for restarting time-in-container tracking every time a card enters a container, even one with the same configured limit as the one it just left.",
      text: "Reset the timer",
      selectedText: "Reset the timer",
      icon: Icons.resetATimer,
    },
  },
  {
    id: "action-showLabelAsTag",
    actionObject: "card",
    actionEventType: "update",
    actionField: "showLabelAsTag",
    actionValue: null,
    actionValueEditable: false,
    buttonOptions: {
      tooltip:
        "Forces the card's label to display as a tag-styled pill, overriding the shop's default for this card.",
      text: "Show label as a tag",
      selectedText: "Show label as a tag",
      icon: Icons.tags,
    },
  },
  {
    id: "action-hideLabelAsTag",
    actionObject: "card",
    actionEventType: "update",
    actionField: "hideLabelAsTag",
    actionValue: null,
    actionValueEditable: false,
    buttonOptions: {
      tooltip:
        "Forces the card's label to NOT display as a tag-styled pill, overriding the shop's default for this card.",
      text: "Hide label as a tag",
      selectedText: "Hide label as a tag",
      icon: Icons.tags,
    },
  },
]);
